import { useEffect, useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useAgentsStore } from '../../stores/agentsStore';
import type { AgentSummary, CycleLogEntry } from '../../types/agents';

interface RecentCycleLogFeedProps {
  agents: AgentSummary[];
}

interface FlatEntry extends CycleLogEntry {
  agent: string;
}

export function RecentCycleLogFeed({ agents }: RecentCycleLogFeedProps) {
  const { cycleLogs, loadCycleLog } = useAgentsStore();

  // Lazy-load every agent's log on mount so the cross-cutting feed has data.
  useEffect(() => {
    for (const agent of agents) {
      if (agent.has_cycle_log && !cycleLogs[agent.name]) {
        void loadCycleLog(agent.name);
      }
    }
  }, [agents, cycleLogs, loadCycleLog]);

  const flat: FlatEntry[] = useMemo(() => {
    const rows: FlatEntry[] = [];
    for (const agent of agents) {
      const entries = cycleLogs[agent.name] ?? [];
      for (const entry of entries.slice(0, 10)) {
        rows.push({ ...entry, agent: agent.name });
      }
    }
    rows.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return rows.slice(0, 20);
  }, [agents, cycleLogs]);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Recent cycle-log activity
      </Typography>
      {flat.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing logged yet.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {flat.map((entry, idx) => (
            <Box
              key={`${entry.agent}-${entry.timestamp}-${idx}`}
              sx={{
                display: 'flex',
                gap: 1,
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                color: entry.skipped ? 'text.secondary' : 'text.primary',
                fontStyle: entry.skipped ? 'italic' : 'normal',
                py: 0.25,
              }}
            >
              <Box component="span" sx={{ color: 'text.disabled', minWidth: 130 }}>
                {entry.timestamp.replace('T', ' ').replace('Z', '')}
              </Box>
              <Box component="span" sx={{ fontWeight: 600, minWidth: 140 }}>
                {entry.agent}
              </Box>
              <Box
                component="span"
                sx={{
                  flex: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {entry.action}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}
